import { useState, useRef, useEffect } from 'react'
import { Check, X, Edit2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'

interface InlineEditProps {
  value: string | number
  onSave: (value: string | number) => Promise<boolean>
  type?: 'text' | 'textarea' | 'number'
  placeholder?: string
  className?: string
  multiline?: boolean
  rows?: number
  disabled?: boolean
}

export function InlineEdit({
  value,
  onSave,
  type = 'text',
  placeholder,
  className = '',
  multiline = false,
  rows = 3,
  disabled = false
}: InlineEditProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(String(value || ''))
  const [isSaving, setIsSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    setEditValue(String(value || ''))
  }, [value])

  useEffect(() => {
    if (isEditing) {
      // Focus the input when entering edit mode
      const element = multiline ? textareaRef.current : inputRef.current
      if (element) {
        element.focus()
        element.select()
      }
    }
  }, [isEditing, multiline])

  const handleSave = async () => {
    if (editValue === String(value)) {
      setIsEditing(false)
      return
    }

    setIsSaving(true)
    try {
      const finalValue = type === 'number' ? Number(editValue) : editValue
      const success = await onSave(finalValue)
      if (success) {
        setIsEditing(false)
      }
    } catch (error) {
      console.error('Error saving inline edit:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    setEditValue(String(value || ''))
    setIsEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault()
      handleSave()
    } else if (e.key === 'Escape') {
      handleCancel()
    } else if (e.key === 'Enter' && e.metaKey && multiline) {
      // Cmd/Ctrl + Enter to save in multiline
      e.preventDefault()
      handleSave()
    }
  }

  if (disabled) {
    return (
      <span className={className}>
        {value || placeholder || '—'}
      </span>
    )
  }

  if (!isEditing) {
    return (
      <div className={`group inline-flex items-center gap-2 ${className}`}>
        <span className="min-w-0 flex-1">
          {value || (
            <span className="text-gray-400 italic">
              {placeholder || 'Klik om toe te voegen'}
            </span>
          )}
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsEditing(true)}
          className="opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 p-0"
        >
          <Edit2 className="h-3 w-3" />
        </Button>
      </div>
    )
  }

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {multiline ? (
        <Textarea
          ref={textareaRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          rows={rows}
          className="flex-1 resize-none"
        />
      ) : (
        <Input
          ref={inputRef}
          type={type}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="flex-1"
        />
      )}
      
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleSave}
          disabled={isSaving}
          className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
        >
          {isSaving ? (
            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-green-600"></div>
          ) : (
            <Check className="h-3 w-3" />
          )}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCancel}
          disabled={isSaving}
          className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
        >
          <X className="h-3 w-3" />
        </Button>
      </div>
    </div>
  )
}